import { $flattenRootToSingleParagraph } from '#pplx/frontend/composer-sdk/lexical/utils/rootFlattening.js';
import { $insertDataTransferForRichText } from '@lexical/clipboard';
import { objectKlassEquals } from '@lexical/utils';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_HIGH, PASTE_COMMAND, PASTE_TAG, } from 'lexical';
const RECENT_CLIPBOARD_PASTE_WINDOW_MS = 50;
export const MAX_CHARACTERS_ALLOWED_TO_PASTE = 40000;
/**
 * Registers the paste behavior that sits in front of Lexical's RichTextPlugin.
 * Normal ClipboardEvent pastes delegate back to RichTextPlugin after the size
 * check; standalone beforeinput DataTransfer pastes are handled here because
 * RichTextPlugin only reads clipboardData from ClipboardEvent payloads.
 */
export function pasteCommand({ editor, }) {
    // Persists across PASTE_COMMAND dispatches for this registration. A normal
    // ClipboardEvent paste records its timestamp so the following duplicate
    // beforeinput paste event can fall through to Lexical's built-in suppression.
    let recentClipboardPasteAt = null;
    return editor.registerCommand(PASTE_COMMAND, (event, activeEditor) => {
        const now = Date.now();
        const clipboardData = getClipboardData(event);
        if (clipboardData !== null) {
            recentClipboardPasteAt = now;
            // Return false when the paste is within the submit limit so Lexical's
            // RichTextPlugin handles the actual ClipboardEvent insertion.
            return preventOversizedPaste(event, clipboardData);
        }
        const inputDataTransfer = getInputDataTransfer(event);
        if (inputDataTransfer === null) {
            return false;
        }
        if (isRecentClipboardPaste(recentClipboardPasteAt, now)) {
            // Chrome/Windows dispatches both a DOM `paste` ClipboardEvent and a
            // `beforeinput insertFromPaste` InputEvent for one Ctrl+V gesture. Let
            // the duplicate InputEvent fall through to Lexical core, which already
            // suppresses it.
            return false;
        }
        if (preventOversizedPaste(event, inputDataTransfer)) {
            return true;
        }
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
            return false;
        }
        // Vivaldi/Linux can deliver paste only through beforeinput.dataTransfer.
        // RichTextPlugin does not insert from InputEvent.dataTransfer, so this
        // standalone path performs the rich paste locally and flattens it back to
        // the single-paragraph input shape.
        event.preventDefault();
        activeEditor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
                return;
            }
            $insertDataTransferForRichText(inputDataTransfer, selection, activeEditor);
            $flattenRootToSingleParagraph();
        }, { tag: PASTE_TAG });
        return true;
    }, COMMAND_PRIORITY_HIGH);
}
function getClipboardData(event) {
    if (objectKlassEquals(event, ClipboardEvent)) {
        return event.clipboardData;
    }
    if ('clipboardData' in event) {
        const clipboardData = event.clipboardData;
        return clipboardData instanceof DataTransfer ? clipboardData : null;
    }
    return null;
}
function getInputDataTransfer(event) {
    if (objectKlassEquals(event, InputEvent)) {
        return event.dataTransfer;
    }
    if ('dataTransfer' in event) {
        const dataTransfer = event.dataTransfer;
        return dataTransfer instanceof DataTransfer ? dataTransfer : null;
    }
    return null;
}
function preventOversizedPaste(event, dataTransfer) {
    const pastedText = dataTransfer.getData('text/plain');
    if (!pastedText || pastedText.length <= MAX_CHARACTERS_ALLOWED_TO_PASTE) {
        return false;
    }
    // Prevent default paste behavior — custom paste handling
    event.preventDefault();
    return true;
}
function isRecentClipboardPaste(handledAt, now) {
    return handledAt !== null && now - handledAt <= RECENT_CLIPBOARD_PASTE_WINDOW_MS;
}
//# sourceMappingURL=pasteCommand.js.map